import React, { useState } from "react";
import { useForm } from "react-hook-form";
import Button from "components/ui/Button";
import { showToast } from "components/ui/toast";
import { useStartMfaSetupMutation, verifyMfaApi } from "apis/auth";

// The whole enrolment flow as one component, rendered in two frames:
//
//   - the forced gate at /mfa-setup, full screen with no way past it
//   - the Two-factor card in /settings, which the admin can walk away from
//
// Keeping it in one place is what stops the two drifting apart.
//
// Three steps, in this order for a reason: the password proves it's really
// them before an authenticator is bound to the account; the code proves the
// authenticator actually works before 2FA is switched on; the backup codes are
// shown last so they can't be skipped past on the way to something else.
export default function MfaEnrolment({ onComplete, onCancel }) {
  const [step, setStep] = useState("password");
  const [setup, setSetup] = useState(null); // { qrDataUrl, secret, backupCodes }
  const [codesSaved, setCodesSaved] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [codeError, setCodeError] = useState("");

  const { mutateAsync: startSetup, isPending: starting } =
    useStartMfaSetupMutation();

  const passwordForm = useForm({ defaultValues: { password: "" } });
  const codeForm = useForm({ defaultValues: { code: "" } });

  const onSubmitPassword = async ({ password }) => {
    try {
      const res = await startSetup(password);
      setSetup(res.data);
      setStep("scan");
    } catch {
      // The mutation already toasts; the form stays put so they can retry.
    }
  };

  const onSubmitCode = async ({ code }) => {
    setVerifying(true);
    setCodeError("");
    try {
      await verifyMfaApi(code.trim());
      setStep("codes");
    } catch (error) {
      setCodeError(
        error?.response?.data?.message ||
          "That code isn't right. Check your authenticator and retry.",
      );
    } finally {
      setVerifying(false);
    }
  };

  const downloadCodes = () => {
    const body = [
      "Beleef admin — two-factor backup codes",
      "",
      "Each code works once. Use one if you lose access to your authenticator.",
      "",
      ...setup.backupCodes,
    ].join("\n");

    const url = URL.createObjectURL(
      new Blob([body], { type: "text/plain;charset=utf-8" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = "beleef-admin-backup-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setCodesSaved(true);
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(setup.backupCodes.join("\n"));
      showToast("success", "Backup codes copied");
      setCodesSaved(true);
    } catch {
      showToast("error", "Couldn't copy — use Download instead");
    }
  };

  // ── Step 1: confirm the password ────────────────────────────────────────
  if (step === "password") {
    return (
      <form
        onSubmit={passwordForm.handleSubmit(onSubmitPassword)}
        className="space-y-4"
      >
        <p className="text-sm text-darkergray">
          Two-factor authentication adds a 6-digit code from your phone to every
          sign-in. Confirm your password to begin.
        </p>
        <div>
          <input
            type="password"
            autoFocus
            autoComplete="current-password"
            placeholder="Your password"
            className={`form-input border ${
              passwordForm.formState.errors.password
                ? "border-red-500"
                : "border-mediumgray"
            }`}
            {...passwordForm.register("password", {
              required: "Password is required",
            })}
          />
          {passwordForm.formState.errors.password && (
            <p className="form-error-message">
              {passwordForm.formState.errors.password.message}
            </p>
          )}
        </div>
        <div className="flex gap-3">
          <Button
            type="submit"
            className="black-button flex-1"
            loading={starting}
            disabled={starting}
          >
            Continue
          </Button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-mediumgray rounded-md text-sm"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    );
  }

  // ── Step 2: scan and prove ──────────────────────────────────────────────
  if (step === "scan") {
    return (
      <form onSubmit={codeForm.handleSubmit(onSubmitCode)} className="space-y-4">
        <p className="text-sm text-darkergray">
          Scan this with Google Authenticator (or any authenticator app), then
          enter the 6-digit code it shows.
        </p>

        <div className="flex justify-center">
          <img
            src={setup.qrDataUrl}
            alt="Two-factor QR code"
            className="w-[220px] h-[220px] border border-mediumgray rounded-md"
          />
        </div>

        {setup.secret && (
          <div className="text-center">
            <p className="text-xs text-darkergray mb-1">
              Can't scan? Enter this key instead:
            </p>
            <code className="text-sm tracking-widest break-all">
              {setup.secret}
            </code>
          </div>
        )}

        <div>
          <input
            type="text"
            autoFocus
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="000000"
            className={`form-input border text-center text-lg tracking-[0.5em] ${
              codeError ? "border-red-500" : "border-mediumgray"
            }`}
            {...codeForm.register("code", {
              required: "Enter the 6-digit code",
              pattern: { value: /^\d{6}$/, message: "Six digits, numbers only" },
            })}
          />
          {(codeError || codeForm.formState.errors.code) && (
            <p className="form-error-message">
              {codeError || codeForm.formState.errors.code.message}
            </p>
          )}
        </div>

        <Button
          type="submit"
          className="w-full black-button"
          loading={verifying}
          disabled={verifying}
        >
          Turn on two-factor
        </Button>
      </form>
    );
  }

  // ── Step 3: backup codes, shown once ────────────────────────────────────
  return (
    <div className="space-y-4">
      <div>
        <h5 className="mb-1">Save your backup codes</h5>
        <p className="text-sm text-darkergray">
          Each code works once, and this is the only time they're shown. Use one
          if you ever lose access to your authenticator.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 p-4 bg-lightergray rounded-md">
        {setup.backupCodes.map((code) => (
          <code key={code} className="text-sm tracking-wider">
            {code}
          </code>
        ))}
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={downloadCodes}
          className="flex-1 px-4 py-2 border border-mediumgray rounded-md text-sm"
        >
          Download
        </button>
        <button
          type="button"
          onClick={copyCodes}
          className="flex-1 px-4 py-2 border border-mediumgray rounded-md text-sm"
        >
          Copy
        </button>
      </div>

      <label className="flex items-start gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={codesSaved}
          onChange={(e) => setCodesSaved(e.target.checked)}
          className="mt-1"
        />
        <span>I've saved these codes somewhere safe</span>
      </label>

      <Button
        type="button"
        className="w-full black-button"
        disabled={!codesSaved}
        onClick={onComplete}
      >
        Done
      </Button>
    </div>
  );
}
